import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Building2,
  Wallet,
  Users,
  CalendarClock,
  MessageSquareText,
  Clock,
  LogOut,
  Stethoscope,
  ChevronLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/clinics", label: "Clinics", icon: Building2 },
  { to: "/approvals", label: "Approvals", icon: Clock },
  { to: "/revenue", label: "Revenue", icon: Wallet },
  { to: "/subscribers", label: "Subscribers", icon: Users },
  { to: "/demo-requests", label: "Demo Requests", icon: CalendarClock },
  { to: "/custom-messaging", label: "Custom Messaging", icon: MessageSquareText },
];

export function Sidebar({ collapsed, onToggle, mobileOpen, onCloseMobile }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const content = (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className={cn("flex h-16 items-center gap-3 border-b border-border/60 px-5", collapsed && "justify-center px-0")}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-lux-emerald/15 text-lux-emerald">
          <Stethoscope className="h-5 w-5" />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <p className="font-heading text-lg font-semibold tracking-tight">CliniQueue</p>
            <p className="text-[11px] font-medium uppercase tracking-widest text-gold">SUPER ADMIN</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1.5 overflow-y-auto p-3">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onCloseMobile}
            className={({ isActive }) =>
              cn(
                "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                collapsed && "justify-center",
                isActive
                  ? "bg-lux-emerald/10 text-lux-emerald"
                  : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="active-nav"
                    className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-lux-emerald"
                  />
                )}
                <item.icon className="h-[18px] w-[18px] shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-border/60 p-3">
        {!collapsed && (
          <div className="mb-3 flex items-center gap-3 rounded-xl bg-secondary/40 p-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15 font-heading text-sm font-semibold text-gold">
              {(user?.name || "SA").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-semibold">{user?.name || "Superadmin"}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email || "admin@cliniqueue.in"}</p>
            </div>
          </div>
        )}
        <button
          data-testid={TID.logoutButton}
          onClick={handleLogout}
          className={cn(
            "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive",
            collapsed && "justify-center"
          )}
        >
          <LogOut className="h-[18px] w-[18px]" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <motion.aside
        data-testid={TID.sidebar}
        animate={{ width: collapsed ? 80 : 264 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="glass sticky top-0 z-40 hidden h-screen shrink-0 lg:block"
      >
        {content}
        <button
          data-testid={TID.sidebarToggle}
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="absolute -right-3 top-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-md transition-colors hover:text-foreground"
        >
          <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
        </button>
      </motion.aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onCloseMobile}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="glass fixed inset-y-0 left-0 z-50 w-64 lg:hidden"
            >
              {content}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
